<script setup lang="ts">
import { computed, ref } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { groupsFor, inboxBadges, ownerSheetFor, ownerTabsFor, type NavSection, type Role } from '../../navigation'
import { useOwnerInboxStore } from '../../stores/ownerInbox'
import { isActive, isAnyActive } from './useActiveNav'
import NavIcon from './NavIcon.vue'
import MoreSheet from './MoreSheet.vue'
import NavBadge from './NavBadge.vue'

const page = usePage()
const inboxStore = useOwnerInboxStore()
const role = computed(() => (page.props.current_user as { role: Role }).role)
const isTeacher = computed(
  () => role.value === 'client' && (page.props.current_user as { client_type?: string }).client_type === 'teacher',
)
const coworking = computed(() => page.props.coworking_available !== false)
const doorOpen = computed(() => page.props.door_access_open !== false)
const groups = computed(() => groupsFor(role.value, coworking.value, doorOpen.value))
// Личные пункты, которым в таб-баре места нет: «Анкета» (Task 11), «Акции» и «Зал» —
// все редкие и уходят в «Ещё». У клиента ради них шит появляется даже без
// разделов «Управление»/«Владелец».
// «Уведомления» тоже всегда в «Ещё»: колокольчик со счётчиком стоит в шапке, а
// седьмой пункт не помещался — на 360px подписи занимали 361px и слипались без
// зазоров (проверено 2026-08-29 на роли преподавателя).
//
// Преподавателю «Анкета» нужна каждый день (по ней его находят в каталоге), поэтому
// у него она вкладка, а «Профиль» уходит в «Ещё»: число вкладок то же, на 360px не больше.
const OVERFLOW_KEYS = ['card', 'promos', 'hall', 'notifications']
const TEACHER_OVERFLOW_KEYS = ['profile', 'promos', 'hall', 'notifications']
const overflowKeys = computed(() => (isTeacher.value ? TEACHER_OVERFLOW_KEYS : OVERFLOW_KEYS))
const personalOverflow = computed(() => groups.value.personal.filter((i) => overflowKeys.value.includes(i.key)))
const isOwner = computed(() => role.value === 'owner')
// Владелец — свой набор (спека 2026-10-11-lk-audit-minor-owner-today): «Сегодня»,
// «Расписание», «Дверь» во вкладках, остальное — двумя группами в «Ещё».
// «Анкета» преподавателя стоит в конце: в общем списке она раньше «Двери» и «Пропусков».
const clientTabs = computed(() => {
  const items = groups.value.personal.filter((i) => !overflowKeys.value.includes(i.key))
  return [...items.filter((i) => i.key !== 'card'), ...items.filter((i) => i.key === 'card')]
})
const tabs = computed(() =>
  isOwner.value ? ownerTabsFor(coworking.value) : clientTabs.value,
)
const sections = computed<NavSection[]>(() =>
  isOwner.value
    ? ownerSheetFor(coworking.value)
    : [
        { key: 'personal', title: 'Личное', items: personalOverflow.value },
        { key: 'management', title: 'Управление', items: groups.value.management },
      ],
)
const sheetItems = computed(() => sections.value.flatMap((s) => s.items))
const hasMore = computed(() => sheetItems.value.length > 0)
const sheetOpen = ref(false)
const moreActive = computed(() => isAnyActive(sheetItems.value))
const badges = computed(() => inboxBadges(inboxStore.counts))

const tabClass = (href: string) =>
  isActive(href)
    ? 'flex min-w-0 flex-1 flex-col items-center gap-0.5 py-1.5 text-[10.5px] font-semibold text-accent'
    : 'flex min-w-0 flex-1 flex-col items-center gap-0.5 py-1.5 text-[10.5px] font-semibold text-muted'
</script>

<template>
  <nav class="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden">
    <component :is="item.external ? 'a' : Link" v-for="item in tabs" :key="item.key" :href="item.href" data-testid="tab"
          :class="tabClass(item.href)" :aria-current="isActive(item.href) ? 'page' : undefined">
      <span class="relative">
        <NavIcon :name="item.icon" class="h-5.5 w-5.5" />
        <NavBadge v-if="badges[item.key]" :label="badges[item.key]" floating />
      </span>
      {{ item.label }}
    </component>
    <button v-if="hasMore" type="button" data-testid="more" @click="sheetOpen = true"
            class="flex min-w-0 flex-1 flex-col items-center gap-0.5 py-1.5 text-[10.5px] font-semibold"
            :class="moreActive ? 'text-accent' : 'text-muted'">
      <NavIcon name="more" class="h-5.5 w-5.5" /> Ещё
    </button>
  </nav>

  <MoreSheet v-if="sheetOpen" :sections="sections" :badges="badges" @close="sheetOpen = false" />
</template>
